import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { useApi } from '@/contexts/AuthContext';
import { Search, ChevronDown, ChevronUp, Scale, HardHat, Landmark, FolderSearch, RefreshCw, CheckCircle } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const sevColor = s => s === 'high' ? C.red : s === 'medium' ? C.amber : C.slate;
const CAT_ICON = { internal_claim: FolderSearch, discovered_claim: FolderSearch, court_record: Scale, worksafe: HardHat, afca: Landmark };

export default function RCDPanel({ entityId }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  const run = useCallback((refresh = false) => {
    setLoading(true);
    api.get(`/entities/${entityId}/rcd${refresh ? '?refresh=1' : ''}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [api, entityId]);
  useEffect(() => { run(); }, [run]);

  const allRecords = data ? [...(data.internal_claims || []), ...(data.records || [])] : [];

  return (
    <Card className="rounded-sm border bg-white" style={{ borderColor: 'rgba(26,76,124,0.2)' }} data-testid="rcd-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#F5F7FA] transition-colors">
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.blue }}>
          <Search className="w-4 h-4" />Reverse Claims Discovery
          {loading ? (
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold normal-case animate-pulse"
              style={{ background: 'rgba(0,180,216,0.1)', color: C.teal }} data-testid="rcd-searching">Searching…</span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold normal-case" data-testid="rcd-match-count"
              style={{ background: allRecords.length ? 'rgba(232,160,32,0.12)' : 'rgba(29,158,117,0.1)', color: allRecords.length ? C.amber : C.green }}>
              {allRecords.length ? `${allRecords.length} match${allRecords.length === 1 ? '' : 'es'}` : 'No records found'}
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4" style={{ color: C.slate }} /> : <ChevronDown className="w-4 h-4" style={{ color: C.slate }} />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          {loading ? (
            <div className="space-y-1.5 py-1" data-testid="rcd-progress">
              {['Internal claims register', 'AustLII / NZLII court records', 'WorkSafe notifications', 'AFCA determinations'].map((s, i) => (
                <p key={s} className="text-[11px] flex items-center gap-2" style={{ color: C.slate }}>
                  <span className="w-3 h-3 rounded-full border border-t-transparent animate-spin inline-block"
                    style={{ borderColor: C.teal, borderTopColor: 'transparent', animationDelay: `${i * 120}ms` }} />
                  Checking {s}…
                </p>
              ))}
            </div>
          ) : !data ? (
            <p className="text-xs py-1" style={{ color: C.slate }}>Claims discovery unavailable for this entity.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-2 text-[10px]" style={{ color: C.slate }}>
                {(data.sources_checked || []).map(s => (
                  <span key={s} className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(136,150,168,0.08)' }}>
                    <CheckCircle className="w-2.5 h-2.5" style={{ color: C.green }} />{s}
                  </span>
                ))}
                <button type="button" onClick={() => run(true)} data-testid="rcd-refresh"
                  className="ml-auto flex items-center gap-1 hover:underline" style={{ color: C.blue }}>
                  <RefreshCw className="w-3 h-3" />Re-run
                </button>
              </div>
              {allRecords.length === 0 ? (
                <p className="text-xs py-1" style={{ color: C.green }} data-testid="rcd-clean">
                  ✓ No prior claims, court records, WorkSafe notifications or AFCA determinations found for {data.legal_name}.
                </p>
              ) : (
                <div className="space-y-2" data-testid="rcd-records">
                  {allRecords.map((r, i) => {
                    const Icon = CAT_ICON[r.category] || FolderSearch;
                    return (
                      <div key={`${r.reference}-${i}`} className="flex gap-2.5 p-2.5 rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.15)' }}
                        data-testid={`rcd-record-${i}`}>
                        <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: sevColor(r.severity) }} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold" style={{ color: C.navy }}>{r.title}</span>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase" style={{ background: sevColor(r.severity) + '15', color: sevColor(r.severity) }}>{r.severity}</span>
                            {r.simulated && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: 'rgba(0,180,216,0.08)', color: C.teal }}>Simulated source</span>
                            )}
                            <span className="text-[10px] font-mono ml-auto" style={{ color: C.slate }}>{r.reference} · {r.date ? String(r.date).slice(0, 10) : ''}</span>
                          </div>
                          <p className="text-[11px] mt-0.5 leading-relaxed" style={{ color: C.slate }}>
                            <b style={{ color: C.blue }}>{r.source}</b>{r.summary ? ` — ${r.summary}` : ''}
                            {r.amount != null && <span className="font-mono"> (${Number(r.amount).toLocaleString('en-AU')})</span>}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {data.external_simulated && allRecords.some(r => r.simulated) && (
                <p className="text-[9px] mt-2 italic" style={{ color: C.slate }}>
                  External court/regulator lookups are simulated until live AustLII, WorkSafe and AFCA data feeds are connected.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}
